/**
 * Nova despesa — e editar despesa (teste físico, item 32) — em dois passos:
 * valor (com quem participa) · divisão.
 *
 * Editar reaproveita a tela inteira: os campos nascem com a despesa
 * existente, e salvar manda a despesa completa com a versão que a tela viu.
 * Não existe mais "despesa privada" (item 34): toda despesa é da viagem.
 *
 * Os passos existem porque a decisão de divisão depende de quem participa, e
 * quem participa depende do valor estar registrado. Pedir tudo numa tela só
 * faria a pessoa preencher a divisão antes de saber entre quantos dividir.
 *
 * O passo 3 mostra a prévia calculada **com a mesma função do servidor**. Se
 * a soma não fechar (percentuais que não dão 100, valores que não batem com o
 * subtotal), o CTA fica bloqueado e a tela diz o que falta — nunca salva um
 * rascunho que o servidor recusaria depois.
 */
import { FAMILIES_NOT_CONFIGURED, canSplitPerFamily, formatMoney, simboloDaMoeda } from '@nv/core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Field } from '../auth/Field';
import { Card, SectionLabel } from '../../ui/primitives';
import type { ExpenseDetail } from './api';
import { cotasPorFamilia, resumoDasCotas } from './divisaoFamilia';
import { CATEGORIAS } from './model';
import { preverDivisao, useFinanceStore, type NovaDespesa } from './store';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { usePessoas } from '../roteiro/people';
import { Sheet } from '../../ui/Sheet';
import { Icon } from '../../ui/icons';
import { Ilustracao } from '../../ui/Ilustracao';

export interface MembroDaViagem {
  memberId: string;
  nome: string;
  /** Ausente enquanto a viagem não tem famílias configuradas. */
  familyId?: string | undefined;
  familyName?: string | undefined;
  familyColor?: string | undefined;
}

const MODOS: { key: NovaDespesa['splitMode']; label: string; nota: string }[] = [
  { key: 'equal', label: 'Igual por pessoa', nota: 'Todo mundo paga a mesma parte.' },
  { key: 'per_family', label: 'Igual por família', nota: 'Cada família carrega uma cota.' },
  { key: 'custom', label: 'Valores personalizados', nota: 'Você define quanto cada um paga.' },
  { key: 'percentage', label: 'Percentual', nota: 'Você define a porcentagem de cada um.' },
];

/** Converte "12,34" ou "12.34" em centavos. Aceita o que a pessoa digita. */
/**
 * Máscara de dinheiro: os dígitos entram como centavos e o campo mostra
 * "1.234,56" enquanto a pessoa digita (decisão do PO, 12/09). Sem dígito,
 * fica vazio — o placeholder "0,00" diz o formato.
 */
export function mascaraDinheiro(texto: string): string {
  const digitos = texto.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  if (!digitos) return '';
  const centavos = digitos.padStart(3, '0');
  const inteiro = centavos.slice(0, -2);
  const fracao = centavos.slice(-2);
  return `${inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${fracao}`;
}

/** "1.234,56" → 123456. Aceita também "1234.56" e "1234,56" (colado). */
export function paraCentavos(texto: string): number {
  const limpo = texto.replace(/\s/g, '');
  // Com vírgula, o ponto é milhar; sem vírgula, um ponto isolado é decimal.
  const normalizado = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo;
  const numero = Number(normalizado);
  if (!Number.isFinite(numero) || numero < 0) return 0;
  return Math.round(numero * 100);
}

/** "12345" (centavos) → "123,45", para os campos nascerem preenchidos na edição. */
const centavosParaTexto = (minor: number) => (minor > 0 ? mascaraDinheiro(String(minor)) : '');

export function NovaDespesaScreen({
  tripId,
  currency,
  membros,
  meuMemberId,
  existente,
}: {
  tripId: string;
  currency: string;
  membros: MembroDaViagem[];
  meuMemberId: string;
  /** Presente no modo editar: a despesa como está no servidor. */
  existente?: ExpenseDetail;
}) {
  const { fotoDe } = usePessoas();
  const navegar = useNavigate();
  const registrar = useFinanceStore((s) => s.registrar);
  const editarDespesa = useFinanceStore((s) => s.editar);
  const editando = Boolean(existente);
  const e = existente?.expense;

  const [passo, setPasso] = useState(1);
  const [valor, setValor] = useState(centavosParaTexto(e?.subtotalMinor ?? 0));
  const [taxa, setTaxa] = useState(centavosParaTexto(e?.taxMinor ?? 0));
  const [gorjeta, setGorjeta] = useState(centavosParaTexto(e?.tipMinor ?? 0));
  const [descricao, setDescricao] = useState(e?.description ?? '');
  const [categoria, setCategoria] = useState(e?.category ?? 'alimentacao');
  const [pagador, setPagador] = useState(existente?.payments[0]?.payerMemberId ?? meuMemberId);
  // Padrão: só quem registra (decisão do PO, 12/09). Adicionar gente é um toque a mais.
  const [participantes, setParticipantes] = useState<string[]>(
    existente ? existente.splits.map((sp) => sp.beneficiaryMemberId).filter((id): id is string => Boolean(id)) : [meuMemberId],
  );
  const [escolhendo, setEscolhendo] = useState(false);
  const [modo, setModo] = useState<NovaDespesa['splitMode']>((e?.splitMode as NovaDespesa['splitMode'] | undefined) ?? 'equal');
  const [avisoDeFamilia, setAvisoDeFamilia] = useState(false);
  const [percentuais, setPercentuais] = useState<Record<string, string>>({});
  const [valores, setValores] = useState<Record<string, string>>(() =>
    e?.splitMode === 'custom' && existente
      ? Object.fromEntries(existente.splits.filter((sp) => sp.beneficiaryMemberId).map((sp) => [sp.beneficiaryMemberId as string, centavosParaTexto(sp.amountMinor)]))
      : {},
  );
  const [salvando, setSalvando] = useState(false);
  // Refino final: salvar termina num selo, não num toast perdido. Guarda o
  // que foi salvo para o resumo (e se ficou na fila, sem rede).
  const [salva, setSalva] = useState<{ resumo: string; pendente: boolean } | null>(null);
  const limparToast = useFinanceStore((s) => s.limparToast);

  const subtotal = paraCentavos(valor);
  const taxMinor = paraCentavos(taxa);
  const tipMinor = paraCentavos(gorjeta);
  const total = subtotal + taxMinor + tipMinor;

  const despesa: NovaDespesa = useMemo(
    () => ({
      description: descricao.trim(),
      category: categoria,
      scope: 'trip',
      subtotalMinor: subtotal,
      taxMinor,
      tipMinor,
      currency,
      splitMode: modo,
      // Editar não muda quando a despesa aconteceu.
      spentAt: e?.spentAt ?? new Date().toISOString(),
      participantMemberIds: participantes,
      payments: [{ payerMemberId: pagador, amountMinor: total }],
      ...(modo === 'percentage'
        ? {
            percentages: Object.fromEntries(
              participantes.map((id) => [id, Number(percentuais[id] ?? 0)]),
            ),
          }
        : {}),
      ...(modo === 'custom'
        ? {
            amounts: Object.fromEntries(
              participantes.map((id) => [id, paraCentavos(valores[id] ?? '')]),
            ),
          }
        : {}),
    }),
    [
      descricao,
      categoria,
      e?.spentAt,
      subtotal,
      taxMinor,
      tipMinor,
      currency,
      modo,
      participantes,
      pagador,
      percentuais,
      valores,
      meuMemberId,
      total,
    ],
  );

  /**
   * Divisão por família exige famílias configuradas.
   *
   * A opção fica **desabilitada** e a tela diz o que fazer. O que ela NÃO faz
   * é trocar a modalidade sozinha: cair para "por pessoa" em silêncio daria à
   * pessoa uma divisão diferente da que ela pediu, com o rótulo da que pediu.
   */
  const familiaDe = useMemo(
    () => Object.fromEntries(membros.map((m) => [m.memberId, m.familyId])),
    [membros],
  );

  const familiasConfiguradas = canSplitPerFamily(
    participantes.map((id) => {
      const familyId = familiaDe[id];
      return familyId ? { id, familyId } : { id };
    }),
  );

  // A prévia recebe as famílias reais: sem elas, `per_family` mostraria aqui a
  // divisão por pessoa e o servidor gravaria outra.
  const previa = useMemo(() => preverDivisao(despesa, familiaDe), [despesa, familiaDe]);

  const cotas = useMemo(
    () =>
      modo === 'per_family' && previa.erro === undefined
        ? cotasPorFamilia(previa.shares, membros)
        : [],
    [modo, previa, membros],
  );

  const podeAvancar =
    passo === 1
      ? subtotal > 0 && descricao.trim().length > 0 && despesa.participantMemberIds.length > 0
      : previa.erro === undefined;

  async function salvar() {
    setSalvando(true);
    try {
      if (existente) {
        await editarDespesa(existente.expense.id, existente.expense.version, despesa);
        navegar(`/financeiro/${existente.expense.id}`, { replace: true });
      } else {
        await registrar(tripId, despesa);
        const pendente = /pendente/i.test(useFinanceStore.getState().toast ?? '');
        // O selo já confirma: o toast não repete na volta ao Financeiro.
        limparToast();
        const pessoas = participantes.length;
        setSalva({
          resumo: `${despesa.description} · ${formatMoney(total, currency)}${pessoas > 1 ? ` · dividida entre ${pessoas}` : ''}`,
          pendente,
        });
      }
    } finally {
      setSalvando(false);
    }
  }

  const nomeDe = (id: string) =>
    membros.find((m) => m.memberId === id)?.nome ?? 'Participante';

  if (salva) {
    return (
      <section className="fin-nova fin-nova--salva" data-testid="despesa-salva">
        <Ilustracao nome="concluido" tamanho={140} className="nv-pop" />
        <h1 className="fin-nova__salva-titulo nv-enter">Despesa salva</h1>
        <p className="fin-nova__salva-texto nv-enter" style={{ ['--i' as string]: 2 }}>
          {salva.resumo}
          {salva.pendente && <><br />Sem rede agora: sincroniza sozinha quando voltar.</>}
        </p>
        <div className="fin__ctas fin-nova__salva-acoes">
          <button type="button" className="nv-btn nv-btn--secondary" onClick={() => navegar('/financeiro/nova', { replace: true })}>
            Nova despesa
          </button>
          <button type="button" className="nv-btn nv-btn--primary" onClick={() => navegar('/financeiro', { replace: true })} data-testid="despesa-ver-financeiro">
            Ver no Financeiro
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="fin-nova">
      <header className="fin-nova__header">
        <span className="fin-nova__passo">Passo {passo} de 2</span>
        <h1 className="fin__titulo fin-nova__titulo">{editando ? 'Editar despesa' : 'Nova despesa'}</h1>
        {/* Dois segmentos que acendem na marca, um por passo. */}
        <span className="fin-nova__passos" aria-hidden="true">
          <span className="fin-nova__passos-seg fin-nova__passos-seg--ativo" />
          <span className={passo >= 2 ? 'fin-nova__passos-seg fin-nova__passos-seg--ativo' : 'fin-nova__passos-seg'} />
        </span>
      </header>

      {passo === 1 && (
        <>
          {/* O valor é o herói da tela: 52/900, centralizado, com a máscara do app. */}
          <div className="fin-nova__valor">
            <span className="fin-nova__moeda">{simboloDaMoeda(currency)}</span>
            <input
              className="fin-nova__valor-input"
              inputMode="decimal"
              placeholder="0,00"
              value={valor}
              onChange={(e) => setValor(mascaraDinheiro(e.target.value))}
              aria-label={`Valor em ${currency}`}
            />
          </div>

          <Field
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            required
          />

          <div className="fin-nova__dupla">
            <Field
              label="Impostos"
              inputMode="decimal"
              value={taxa}
              onChange={(e) => setTaxa(mascaraDinheiro(e.target.value))}
              placeholder="0,00"
            />
            <Field
              label="Gorjeta"
              inputMode="decimal"
              value={gorjeta}
              onChange={(e) => setGorjeta(mascaraDinheiro(e.target.value))}
              placeholder="0,00"
            />
          </div>

          <SectionLabel>CATEGORIA</SectionLabel>
          <div className="fin-nova__chips">
            {Object.entries(CATEGORIAS).map(([chave, c]) => (
              <button
                key={chave}
                type="button"
                className={categoria === chave ? 'fin-chip fin-chip--ativo' : 'fin-chip'}
                onClick={() => setCategoria(chave)}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Quem pagou e quem participa num card só. Quem participa vive
              aqui, não num passo obrigatório: o padrão é só quem registra;
              quem quer dividir toca em "Adicionar pessoas". */}
          <Card variant="surf" lista className="fin-nova__quem-card">
            <label className="fin-nova__linha">
              <FotoAvatar nome={nomeDe(pagador)} url={fotoDe(pagador).url} cor={fotoDe(pagador).cor} tamanho={36} />
              <span className="fin-nova__linha-texto">
                <span className="fin-nova__linha-rotulo">Quem pagou</span>
                <select
                  className="fin-nova__select"
                  value={pagador}
                  onChange={(e) => setPagador(e.target.value)}
                  aria-label="Quem pagou"
                >
                  {membros.map((m) => (
                    <option key={m.memberId} value={m.memberId}>
                      {m.memberId === meuMemberId ? `${m.nome} (você)` : m.nome}
                    </option>
                  ))}
                </select>
              </span>
              <Icon name="avancar" size={18} />
            </label>
            <div className="fin-nova__linha" data-testid="fin-quem">
              <span className="nv-avatar-stack fin-nova__pilha" aria-hidden="true">
                {participantes.slice(0, 3).map((id) => (
                  <FotoAvatar key={id} nome={nomeDe(id)} url={fotoDe(id).url} cor={fotoDe(id).cor} tamanho={28} />
                ))}
              </span>
              <span className="fin-nova__linha-texto">
                <span className="fin-nova__linha-rotulo">Quem participa</span>
                <span className="fin-nova__linha-valor">
                  {participantes.length <= 1
                    ? 'Só você'
                    : `Você e ${participantes.length - 1} · ${participantes
                        .filter((id) => id !== meuMemberId)
                        .map((id) => nomeDe(id).split(/\s+/)[0])
                        .slice(0, 3)
                        .join(', ')}${participantes.length > 4 ? '…' : ''}`}
                </span>
              </span>
              <button type="button" className="nv-btn nv-btn--chip" onClick={() => setEscolhendo(true)} data-testid="fin-adicionar-pessoas">
                {participantes.length <= 1 ? 'Adicionar pessoas' : 'Mudar'}
              </button>
            </div>
          </Card>
          {escolhendo && (
            <Sheet titleId="fin-quem-titulo" onFechar={() => setEscolhendo(false)}>
                <h2 className="sheet__title" id="fin-quem-titulo">Quem participa</h2>
                <div className="fin-nova__chips">
                  <button type="button" className="fin-chip" onClick={() => setParticipantes(membros.map((m) => m.memberId))}>
                    Todos · {membros.length}
                  </button>
                  <button type="button" className="fin-chip" onClick={() => setParticipantes([meuMemberId])}>
                    Só eu
                  </button>
                </div>
                <div className="fin-nova__pessoas">
                  {membros.map((m) => {
                    const marcado = participantes.includes(m.memberId);
                    return (
                      <label className="fin-nova__pessoa" key={m.memberId}>
                        <input
                          type="checkbox"
                          checked={marcado}
                          disabled={m.memberId === meuMemberId && marcado && participantes.length === 1}
                          onChange={() =>
                            setParticipantes((atual) =>
                              marcado ? atual.filter((id) => id !== m.memberId) : [...atual, m.memberId],
                            )
                          }
                        />
                        <FotoAvatar nome={m.nome} url={fotoDe(m.memberId).url} cor={fotoDe(m.memberId).cor} tamanho={24} />
                        <span>{m.nome}{m.memberId === meuMemberId ? ' (você)' : ''}</span>
                      </label>
                    );
                  })}
                </div>
                <button type="button" className="nv-btn nv-btn--primary nv-btn--block" disabled={participantes.length === 0} onClick={() => setEscolhendo(false)}>
                  Pronto{participantes.length > 1 ? ` · ${participantes.length} pessoas` : ''}
                </button>
            </Sheet>
          )}
        </>
      )}

      {passo === 2 && (
        <>
          <SectionLabel>Como dividir</SectionLabel>
            <div className="fin-nova__modos">
              {MODOS.map((m) => {
                const bloqueado = m.key === 'per_family' && !familiasConfiguradas;
                return (
                  <label
                    className={bloqueado ? 'fin-nova__modo fin-nova__modo--bloqueado' : 'fin-nova__modo'}
                    key={m.key}
                  >
                    <input
                      type="radio"
                      name="modo-divisao"
                      checked={modo === m.key}
                      disabled={bloqueado}
                      onChange={() => setModo(m.key)}
                      {...(bloqueado ? { 'aria-describedby': 'aviso-familia' } : {})}
                    />
                    <span
                      onClick={bloqueado ? () => setAvisoDeFamilia(true) : undefined}
                      role={bloqueado ? 'button' : undefined}
                      tabIndex={bloqueado ? 0 : undefined}
                      onKeyDown={
                        bloqueado
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') setAvisoDeFamilia(true);
                            }
                          : undefined
                      }
                    >
                      <span className="fin-nova__modo-label">
                        {m.label}
                        {bloqueado && (
                          <span className="fin-nova__cadeado" aria-hidden="true">
                            {' '}
                            🔒
                          </span>
                        )}
                      </span>
                      <span className="fin-nova__modo-nota">
                        {bloqueado ? FAMILIES_NOT_CONFIGURED : m.nota}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

          {modo === 'percentage' && (
            <Card variant="surf">
              {participantes.map((id) => (
                <div className="fin-nova__linha-ajuste" key={id}>
                  <span>{nomeDe(id)}</span>
                  <input
                    className="fin-nova__ajuste"
                    inputMode="decimal"
                    value={percentuais[id] ?? ''}
                    onChange={(e) =>
                      setPercentuais((a) => ({ ...a, [id]: e.target.value }))
                    }
                    aria-label={`Percentual de ${nomeDe(id)}`}
                  />
                  <span aria-hidden="true">%</span>
                </div>
              ))}
            </Card>
          )}

          {modo === 'custom' && (
            <Card variant="surf">
              {participantes.map((id) => (
                <div className="fin-nova__linha-ajuste" key={id}>
                  <span>{nomeDe(id)}</span>
                  <input
                    className="fin-nova__ajuste"
                    inputMode="decimal"
                    value={valores[id] ?? ''}
                    onChange={(e) => setValores((a) => ({ ...a, [id]: mascaraDinheiro(e.target.value) }))}
                    aria-label={`Valor de ${nomeDe(id)}`}
                  />
                </div>
              ))}
            </Card>
          )}

          {cotas.length > 0 && (
            <>
              <SectionLabel>POR FAMÍLIA</SectionLabel>
              <Card variant="surf">
                <ul className="fin-familia__lista" aria-label="Cota por família">
                  {cotas.map((c) => (
                    <li className="fin-familia__linha" key={c.chave}>
                      <span
                        className="fin-familia__ponto"
                        style={{ background: c.cor }}
                        aria-hidden="true"
                      />
                      <span className="fin-familia__nome">
                        {c.nome}
                        <span className="fin-familia__membros">
                          {c.avulso
                            ? 'sem família · conta como uma unidade'
                            : c.membros === 1
                              ? '1 pessoa nesta despesa'
                              : `${c.membros} pessoas nesta despesa`}
                        </span>
                      </span>
                      <span className="fin-familia__valor">
                        {formatMoney(c.totalMinor, currency)}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="fin-familia__resumo">{resumoDasCotas(cotas, currency)}</p>
              </Card>
            </>
          )}

          {!familiasConfiguradas && (
            <Card variant="chip">
              <p className="auth-note" id="aviso-familia" role={avisoDeFamilia ? 'alert' : undefined}>
                {FAMILIES_NOT_CONFIGURED} Até lá, a modalidade fica indisponível
                — e a despesa não é dividida por pessoa no lugar dela.
              </p>
            </Card>
          )}

          {previa.erro ? (
            // O CTA fica bloqueado: melhor dizer o que falta do que salvar
            // um rascunho que o servidor recusaria.
            <Card variant="chip">
              <p className="auth-error" role="alert">
                {previa.erro}
              </p>
            </Card>
          ) : (
            <div className="fin-nova__resumo">
              <span className="fin-nova__resumo-linha">
                <span className="fin-nova__resumo-rotulo">Total</span>
                <span className="fin-nova__resumo-total">{formatMoney(previa.totalMinor, currency)}</span>
              </span>
              {previa.shares.length > 0 && (
                <span className="fin-nova__resumo-linha">
                  <span className="fin-nova__resumo-rotulo">
                    {previa.shares.length === 1 ? 'Só você' : modo === 'equal' ? `Cada pessoa (${previa.shares.length})` : `${previa.shares.length} pessoas`}
                  </span>
                  {modo === 'equal' && previa.shares.length > 1 && (
                    <span className="fin-nova__resumo-cada">{formatMoney(previa.shares[0]!.total, currency)}</span>
                  )}
                </span>
              )}
            </div>
          )}
        </>
      )}

      <div className="fin__ctas">
        {passo > 1 && (
          <button
            type="button"
            className="nv-btn nv-btn--secondary"
            onClick={() => setPasso((p) => p - 1)}
          >
            Voltar
          </button>
        )}
        {passo < 2 ? (
          <button
            type="button"
            className="nv-btn nv-btn--primary"
            disabled={!podeAvancar}
            onClick={() => setPasso((p) => p + 1)}
          >
            Continuar
          </button>
        ) : (
          <button
            type="button"
            className="nv-btn nv-btn--primary"
            disabled={!podeAvancar || salvando}
            onClick={() => void salvar()}
          >
            {salvando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Salvar despesa'}
          </button>
        )}
      </div>
    </section>
  );
}
